import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

export default function SubscriptionTracker() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => { setTimeout(() => setLoading(false), 500); }, []);

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: user?.currency || 'USD' }).format(v || 0);

  const plans = [
    { name: 'Basic', price: 0, features: ['Income Tracking', 'Expense Tracking', 'Basic Dashboard'], color: 'gray' },
    { name: 'Premium', price: 9.99, features: ['All Basic features', 'Budget Planner', 'Savings Goals', 'Net Worth', 'Advanced Charts', 'Export Reports'], color: 'indigo', popular: true },
    { name: 'Pro', price: 19.99, features: ['All Premium features', 'Multi-currency', 'API Access', 'Priority Support', 'Custom Reports', 'Team Access'], color: 'purple' },
  ];

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div></div>;

  const isActive = user?.subscriptionStatus === 'active';
  const currentPlan = user?.subscriptionPlan || 'none';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Subscription</h1>
        <p className="text-gray-500">Manage your FinovaTrack plan</p>
      </div>

      {isActive ? (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-2xl">⭐</div>
            <div>
              <h2 className="text-xl font-bold">{currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan Active</h2>
              <p className="text-indigo-200">You have full access to all features</p>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">✅ Unlimited Access</span>
            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">✅ Priority Support</span>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <div className="flex items-center gap-3">
            <span className="text-3xl">⚠️</span>
            <div>
              <h3 className="font-semibold text-amber-800">No Active Subscription</h3>
              <p className="text-amber-600 text-sm">Subscribe to unlock all premium features</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map(plan => (
          <div key={plan.name} className={`bg-white rounded-xl shadow-sm border-2 ${plan.popular ? 'border-indigo-500' : 'border-gray-100'} p-6 relative ${plan.popular ? 'scale-105' : ''}`}>
            {plan.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-medium">Most Popular</span>}
            <h3 className="text-xl font-bold text-gray-900 mt-2">{plan.name}</h3>
            <p className="text-4xl font-bold text-gray-900 mt-2">{formatCurrency(plan.price)}<span className="text-lg text-gray-500 font-normal">/mo</span></p>
            <ul className="mt-4 space-y-2">
              {plan.features.map(f => <li key={f} className="flex items-center gap-2 text-sm text-gray-600"><span className="text-green-500">✓</span> {f}</li>)}
            </ul>
            <button onClick={() => { /* Subscribe logic */ toast.success('Subscription activated!'); }}
              disabled={isActive && currentPlan === plan.name.toLowerCase()}
              className={`mt-6 w-full py-2.5 rounded-lg font-medium transition-colors ${isActive && currentPlan === plan.name.toLowerCase() ? 'bg-green-100 text-green-700' : plan.popular ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {isActive && currentPlan === plan.name.toLowerCase() ? '✓ Current Plan' : plan.price === 0 ? 'Get Started' : 'Subscribe'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
